import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spin, Tooltip as AntTooltip, message } from 'antd';
import { TbPolygon, TbRulerMeasure } from 'react-icons/tb';
import { SearchOutlined } from '@ant-design/icons';
import { MdCenterFocusStrong, MdRefresh } from 'react-icons/md';
import { FeatureGroup, MapContainer, Marker, Polyline, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CurrentLocationButton, MapRefCapture, ModuleSlider, ThemeToggleButton, TraffixTileLayer, ZoomButtons,
} from '../../common/map';
import { useAuth } from '../../contexts/AuthContext';
import { loadSemafixCatalog } from './APIs/semafixData';
import { loadOptimizedPlan, saveOptimizedPlan } from './APIs/intersectionEditorApi';
import { DEFAULT_NODE_TYPE_FILTERS, normalizeCatalog } from './model/catalog';
import { flowAt } from './model/forecast';
import { roadWeight, vcColor, vcRatio } from './model/capacity';
import { MAP_SELECTED_COLOR, selectionAwareColor } from '../../constants/scoreScale';
import { intersectionsInside, leafletPolygonToGeoJSON } from './model/geometry';
import { optimizeSignalPlan } from './model/signals';
import { bearingBetween, DEFAULT_PROGRESSION_KPH, computeGreenWave, distanceMeters } from './model/greenWave';
import { writeSemafixHandoff } from './model/syncfixHandoff';
import { IntersectionPanel, PedestrianPanel, SegmentPanel } from './components/SemafixPanels';
import SemafixFilterBar from './components/SemafixFilterBar';
import SemafixLegend, { SemafixFlowLegend } from './components/SemafixLegend';
import { GreenWaveCard, SyncfixConfigModal, SyncfixModeBar } from './components/GreenWavePanels';
import { crossingIcon, intersectionIcon, TlGreenWaveIcon } from './components/mapIcons';
import IntersectionEditor from './components/IntersectionEditor';
import MeasurementLayer, { newMeasurementId, useDistanceMeasurement } from './components/MeasurementLayer';
import MeasurementList from './components/MeasurementList';
import '../../styles/mappa.css';
import './SemafixPage.css';

const CENTER = [44.1024, 9.824];
const MANUAL_SIGNALS_PREFIX = 'semafix.manualSignals.';
const manualSignalsKey = () => `${MANUAL_SIGNALS_PREFIX}${localStorage.getItem('company') || 'default'}`;
const readManualSignals = () => {
  try {
    const value = JSON.parse(localStorage.getItem(manualSignalsKey()) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};
const writeManualSignals = (items) => localStorage.setItem(manualSignalsKey(), JSON.stringify(items));
/** The control cluster lives inside the map, so its clicks must not measure. */
const isolateFromMapClicks = (node) => {
  if (!node) return;
  L.DomEvent.disableClickPropagation(node);
  L.DomEvent.on(node, 'click', L.DomEvent.stopPropagation);
};
const EMPTY_CATALOG = normalizeCatalog();

export default function SemafixPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasService } = useAuth();
  const [messageApi, contextHolder] = message.useMessage();
  const [catalog, setCatalog] = useState(EMPTY_CATALOG);
  const [loading, setLoading] = useState(true);
  const [mapTheme, setMapTheme] = useState('dark');
  const [flowsVisible, setFlowsVisible] = useState(true);
  const [forecastMinute, setForecastMinute] = useState(0);
  const [areaPolygon, setAreaPolygon] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [addingTrafficLight, setAddingTrafficLight] = useState(false);
  const [selectedRoad, setSelectedRoad] = useState(null);
  const [highlightedRoadId, setHighlightedRoadId] = useState(null); // street highlighted from search (§2)
  const [selectedIntersection, setSelectedIntersection] = useState(null);
  const [selectedCrossing, setSelectedCrossing] = useState(null);
  const [nodeTypeFilters, setNodeTypeFilters] = useState(DEFAULT_NODE_TYPE_FILTERS);
  const [plan, setPlan] = useState({ green: 30, yellow: 3, red: 30 });
  const [optimized, setOptimized] = useState(null);
  const [greenWaveMode, setGreenWaveMode] = useState(false);
  const [greenWaveSelection, setGreenWaveSelection] = useState([]);
  const [greenWaveStops, setGreenWaveStops] = useState(null);
  const [progressionSpeed, setProgressionSpeed] = useState(DEFAULT_PROGRESSION_KPH);
  const [editorIntersection, setEditorIntersection] = useState(null);
  /** Signal plan shown in the popup when the editor was opened. */
  const [editorPlan, setEditorPlan] = useState(null);
  const [optimizedPlans, setOptimizedPlans] = useState({});
  /** Distances measured from either map, shared with the editor and the handoff. */
  const [measurements, setMeasurements] = useState([]);
  const [measuring, setMeasuring] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [syncfixDraft, setSyncfixDraft] = useState(null);
  const [mainMap, setMainMap] = useState(null);
  const mapRef = useRef(null);
  const areaGroupRef = useRef(null);
  const areaDrawerRef = useRef(null);

  useEffect(() => {
    let active = true;
    loadSemafixCatalog()
      .then((raw) => {
        if (!active) return;
        const normalized = normalizeCatalog(raw);
        const manual = readManualSignals();
        if (manual.length) {
          normalized.intersections.push(...manual.map((item) => item.intersection));
          normalized.signals.push(...manual.map((item) => item.signal));
          normalized.signalByIntersection = new Map(
            normalized.signals.map((item) => [item.intersectionId, item]),
          );
        }
        setCatalog(normalized);
        setOptimizedPlans(Object.fromEntries(normalized.signals.map((signal) => [
          signal.intersectionId,
          loadOptimizedPlan(signal.intersectionId) || signal.optimizedPlan,
        ]).filter(([, value]) => Boolean(value))));
      })
      .catch(() => { if (active) messageApi.error('Impossibile caricare i dati SEMAFIX'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [messageApi]);

  useEffect(() => {
    const handlePlan = ({ detail }) => {
      setOptimizedPlans((current) => ({ ...current, [detail.intersectionId]: detail.plan }));
    };
    window.addEventListener('semafix:optimized-plan', handlePlan);
    return () => window.removeEventListener('semafix:optimized-plan', handlePlan);
  }, []);

  const visibleIntersections = useMemo(() => (
    intersectionsInside(catalog.intersections, areaPolygon)
      .filter((intersection) => catalog.signalByIntersection.has(intersection.id))
  ), [areaPolygon, catalog.intersections, catalog.signalByIntersection]);
  const visibleSignalIds = useMemo(() => new Set(
    visibleIntersections
      .map((item) => catalog.signalByIntersection.get(item.id)?.id)
      .filter(Boolean),
  ), [catalog.signalByIntersection, visibleIntersections]);

  const nodeTypeCounts = useMemo(() => visibleIntersections.reduce((counts, item) => {
    const key = item.geometryType || 'altro';
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, { T: 0, rotatoria: 0, cross: 0, altro: 0 }), [visibleIntersections]);

  const toggleNodeType = useCallback((key, checked) => {
    setNodeTypeFilters((current) => ({ ...current, [key]: checked }));
  }, []);

  useEffect(() => {
    if (!mainMap || !addingTrafficLight || !areaPolygon) return undefined;
    const handleAdd = ({ latlng }) => {
      const candidate = {
        id: `manual-${Date.now()}`,
        name: 'Semaforo aggiunto manualmente',
        lat: latlng.lat,
        lng: latlng.lng,
        type: 'semaforizzata',
        geometryType: 'altro',
        satelliteAvailable: false,
        arms: [],
        maneuversPreview: [],
      };
      if (!intersectionsInside([candidate], areaPolygon).length) {
        messageApi.warning('Aggiungi il semaforo all’interno dell’area disegnata.');
        return;
      }
      const signal = {
        id: `signal-${candidate.id}`,
        intersectionId: candidate.id,
        name: candidate.name,
        lat: candidate.lat,
        lng: candidate.lng,
        green: 30,
        yellow: 3,
        red: 30,
      };
      writeManualSignals([...readManualSignals(), { intersection: candidate, signal }]);
      setCatalog((current) => {
        const intersections = [...current.intersections, candidate];
        const signals = [...current.signals, signal];
        return {
          ...current,
          intersections,
          signals,
          signalByIntersection: new Map(signals.map((item) => [item.intersectionId, item])),
        };
      });
      setAddingTrafficLight(false);
      setSelectedRoad(null);
      setSelectedCrossing(null);
      setSelectedIntersection(candidate);
      setPlan({ green: signal.green, yellow: signal.yellow, red: signal.red });
      messageApi.success('Semaforo aggiunto. Puoi aprire l’editor per configurarlo.');
    };
    mainMap.on('click', handleAdd);
    return () => mainMap.off('click', handleAdd);
  }, [addingTrafficLight, areaPolygon, mainMap, messageApi]);

  const selectedSignal = selectedIntersection
    ? catalog.signalByIntersection.get(selectedIntersection.id)
    : null;

  const signalCrossings = useMemo(
    () => (selectedSignal ? catalog.crossings.filter((item) => item.signalId === selectedSignal.id) : []),
    [catalog.crossings, selectedSignal],
  );

  const armMetrics = useMemo(() => (selectedIntersection?.arms || []).map((arm) => {
    const road = catalog.roadById.get(arm.roadId);
    const flow = flowAt(road || { id: arm.roadId, base: arm.flow }, forecastMinute);
    return { ...arm, flow, capacity: arm.cap, vc: vcRatio(flow, arm.cap) };
  }), [catalog.roadById, forecastMinute, selectedIntersection]);

  const roadFlow = selectedRoad ? flowAt(selectedRoad, forecastMinute) : 0;
  const roadVc = selectedRoad ? vcRatio(roadFlow, selectedRoad.cap) : 0;

  const focusedRoadId = highlightedRoadId || selectedRoad?.id || null;
  const focusedRoad = focusedRoadId
    ? (catalog.roadById.get(focusedRoadId) || (selectedRoad?.id === focusedRoadId ? selectedRoad : null))
    : null;
  const hasMapSelection = !greenWaveMode && Boolean(focusedRoadId || selectedIntersection || selectedCrossing);

  const greenWave = useMemo(
    () => computeGreenWave(greenWaveStops, { speedKph: progressionSpeed }),
    [greenWaveStops, progressionSpeed],
  );

  const fitNetwork = useCallback(() => {
    if (!mapRef.current || catalog.roads.length === 0) return;
    const bounds = L.latLngBounds();
    catalog.roads.forEach((road) => road.co.forEach((point) => bounds.extend(point)));
    if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [30, 30] });
  }, [catalog.roads]);

  useEffect(() => {
    if (!loading && catalog.roads.length) fitNetwork();
  }, [catalog.roads.length, fitNetwork, loading]);

  useEffect(() => {
    if (!editorIntersection) return undefined;
    const timer = setTimeout(() => {
      mapRef.current?.invalidateSize();
      mapRef.current?.flyTo([editorIntersection.lat, editorIntersection.lng], 18);
    }, 220);
    return () => clearTimeout(timer);
  }, [editorIntersection]);

  const selectIntersection = useCallback((intersection) => {
    setSelectedRoad(null);
    setSelectedCrossing(null);
    setHighlightedRoadId(null);
    setSelectedIntersection(intersection);
    const signal = catalog.signalByIntersection.get(intersection.id);
    const saved = optimizedPlans[intersection.id];
    const firstPhase = saved?.phases?.[0];
    if (firstPhase) {
      setPlan({
        green: firstPhase.green,
        yellow: firstPhase.yellow ?? saved.yellow,
        red: firstPhase.red,
      });
      setOptimized({
        ...saved,
        green: firstPhase.green,
        yellow: firstPhase.yellow ?? saved.yellow,
        red: firstPhase.red,
        cycle: saved.cycle || firstPhase.green + (firstPhase.yellow ?? saved.yellow) + firstPhase.red,
      });
    } else if (signal) {
      setPlan({ green: signal.green, yellow: signal.yellow, red: signal.red });
      setOptimized(null);
    }
  }, [catalog.signalByIntersection, optimizedPlans]);

  useEffect(() => {
    if (loading) return;
    const params = new URLSearchParams(location.search);
    const requestedId = params.get('intersectionId');
    const hasCoords = params.has('lat') && params.has('lng');
    const lat = hasCoords ? Number(params.get('lat')) : NaN;
    const lng = hasCoords ? Number(params.get('lng')) : NaN;
    let match = requestedId ? catalog.intersections.find((item) => item.id === requestedId) : null;
    if (!match && Number.isFinite(lat) && Number.isFinite(lng)) {
      match = catalog.intersections.reduce((nearest, item) => {
        const distance = Math.hypot(item.lat - lat, item.lng - lng);
        return !nearest || distance < nearest.distance ? { item, distance } : nearest;
      }, null)?.item;
    }
    if (match) {
      selectIntersection(match);
      mapRef.current?.flyTo([match.lat, match.lng], 17);
    }
  }, [catalog.intersections, loading, location.search, selectIntersection]);

  /** Completing a measure disarms the ruler on both maps. */
  const addMeasurement = useCallback((measurement) => {
    setMeasurements((current) => [...current, measurement]);
    setMeasuring(false);
    messageApi.success(`Distanza misurata: ${Math.round(measurement.distanceMeters)} m`);
  }, [messageApi]);

  const updateMeasurement = useCallback((id, meters) => {
    const value = Number(meters);
    if (!Number.isFinite(value) || value <= 0) return;
    setMeasurements((current) => current.map((item) => (
      item.id === id ? { ...item, distanceMeters: value, manual: true } : item
    )));
  }, []);

  /** A tratta length typed by hand, without going through the ruler. */
  const addManualMeasurement = useCallback((meters) => {
    const value = Number(meters);
    if (!Number.isFinite(value) || value <= 0) return;
    setMeasurements((current) => [...current, {
      id: newMeasurementId(),
      from: null,
      to: null,
      distanceMeters: value,
      manual: true,
    }]);
  }, []);

  const removeMeasurement = useCallback((id) => {
    setMeasurements((current) => current.filter((item) => item.id !== id));
  }, []);

  const undoMeasurement = useCallback(() => {
    setMeasurements((current) => current.slice(0, -1));
  }, []);

  const clearMeasurements = useCallback(() => setMeasurements([]), []);

  const { start: measureStart, cursor: measureCursor } = useDistanceMeasurement({
    map: mainMap,
    active: measuring,
    onComplete: addMeasurement,
  });

  const cancelAreaDraw = useCallback(() => {
    areaDrawerRef.current?.disable();
    areaDrawerRef.current = null;
    setDrawing(false);
  }, []);

  /** Single flag for both maps: the ruler of either view arms them together. */
  const changeMeasuring = useCallback((next) => {
    if (next) cancelAreaDraw();
    setMeasuring(next);
  }, [cancelAreaDraw]);

  const startAreaDraw = () => {
    if (!mapRef.current || !areaGroupRef.current) return;
    setMeasuring(false);
    const map = mapRef.current;
    const drawer = new L.Draw.Polygon(map, {
      showArea: false,
      shapeOptions: { color: '#A1E430', weight: 2, fillColor: '#A1E430', fillOpacity: 0.08 },
    });
    const onCreated = (event) => {
      areaGroupRef.current.clearLayers();
      areaGroupRef.current.addLayer(event.layer);
      setAreaPolygon(leafletPolygonToGeoJSON(event.layer.getLatLngs()[0]));
      setDrawing(false);
      map.fitBounds(event.layer.getBounds(), { padding: [40, 40] });
      drawer.disable();
      areaDrawerRef.current = null;
      map.off(L.Draw.Event.CREATED, onCreated);
    };
    map.on(L.Draw.Event.CREATED, onCreated);
    drawer.enable();
    areaDrawerRef.current = drawer;
    setDrawing(true);
  };

  const cancelGreenWave = useCallback(() => {
    setGreenWaveMode(false);
    setGreenWaveSelection([]);
    setGreenWaveStops(null);
    setSyncfixDraft(null);
  }, []);

  const clearArea = () => {
    areaGroupRef.current?.clearLayers();
    setAreaPolygon(null);
    setDrawing(false);
    setAddingTrafficLight(false);
    setSelectedIntersection(null);
    cancelGreenWave();
  };

  const reset = () => {
    clearArea();
    setSelectedRoad(null);
    setSelectedCrossing(null);
    setHighlightedRoadId(null);
    setNodeTypeFilters(DEFAULT_NODE_TYPE_FILTERS);
    setForecastMinute(0);
    setFlowsVisible(true);
    setOptimized(null);
    setProgressionSpeed(DEFAULT_PROGRESSION_KPH);
    setEditorIntersection(null);
    setEditorPlan(null);
    setMeasurements([]);
    setMeasuring(false);
    setAddingTrafficLight(false);
    fitNetwork();
  };

  const optimizeCorridorStop = (intersectionId) => {
    const intersection = catalog.intersections.find((item) => item.id === intersectionId);
    if (!intersection) return;
    const signal = catalog.signalByIntersection.get(intersection.id);
    const saved = optimizedPlans[intersection.id];
    const basePlan = {
      green: saved?.phases?.[0]?.green ?? signal?.green ?? 30,
      yellow: saved?.yellow ?? signal?.yellow ?? 3,
      red: saved?.phases?.[0]?.red ?? signal?.red ?? 30,
    };
    const arms = (intersection.arms || []).map((arm) => {
      const flow = flowAt(catalog.roadById.get(arm.roadId) || { id: arm.roadId, base: arm.flow }, forecastMinute);
      return { ...arm, flow, capacity: arm.cap, vc: vcRatio(flow, arm.cap) };
    });
    const crossings = catalog.crossings.filter((item) => item.signalId === signal?.id);
    const result = optimizeSignalPlan(basePlan, arms, crossings);
    const savedPlan = saveOptimizedPlan(intersection.id, {
      ...result,
      source: 'optimized',
      phases: (intersection.maneuversPreview || []).map((maneuver) => ({
        maneuverId: maneuver.id,
        nome: maneuver.title,
        green: result.green,
        yellow: result.yellow,
        red: result.red,
      })),
    });
    setOptimizedPlans((current) => ({ ...current, [intersection.id]: savedPlan }));
    setSyncfixDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        stops: current.stops.map((stop) => (
          stop.id === intersection.id
            ? {
              ...stop,
              green: result.green,
              yellow: result.yellow,
              red: result.red,
              timingSource: 'latest-optimized-plan',
            }
            : stop
        )),
      };
    });
    if (intersection.id === selectedIntersection?.id) setOptimized(result);
    messageApi.success(`${signal?.name || intersection.name || 'Semaforo'}: piano ottimizzato`);
  };

  /**
   * SYNCFIX selection mode: the corridor can only be built inside an extracted area
   * and only from signalized intersections.
   */
  const activateGreenWave = useCallback((seedIntersection = null) => {
    const signalized = visibleIntersections.filter((item) => item.type === 'semaforizzata');
    if (!areaPolygon || signalized.length < 2) {
      messageApi.info('Disegna prima un’area contenente almeno due semafori.');
      return;
    }
    setSelectedRoad(null);
    setSelectedCrossing(null);
    setSelectedIntersection(null);
    setGreenWaveStops(null);
    setGreenWaveSelection(seedIntersection?.type === 'semaforizzata' ? [seedIntersection.id] : []);
    setGreenWaveMode(true);
  }, [areaPolygon, messageApi, visibleIntersections]);

  const toggleGreenWaveSelection = (intersection) => {
    if (intersection.type !== 'semaforizzata') {
      messageApi.info('Per SYNCFIX seleziona solo intersezioni semaforizzate.');
      return;
    }
    setGreenWaveStops(null);
    setGreenWaveSelection((current) => (current.includes(intersection.id)
      ? current.filter((id) => id !== intersection.id)
      : [...current, intersection.id]));
  };

  /** Keeps the SEMAFIX context (timings, crossings, corridor flows) attached to each stop. */
  const buildCorridorStop = useCallback((intersection, timingOverride = null, corridorBearing = null) => {
    const signal = catalog.signalByIntersection.get(intersection.id);
    const savedPlan = optimizedPlans[intersection.id];
    const preview = intersection.maneuversPreview || [];
    const movement = corridorBearing == null ? preview[0] : preview.reduce((best, item) => {
      const delta = Math.abs((((Number(item.approachBearing) - corridorBearing) + 540) % 360) - 180);
      return !best || delta < best.delta ? { item, delta } : best;
    }, null)?.item;
    const savedPhase = savedPlan?.phases?.find((phase) => (
      String(phase.maneuverId) === String(movement?.id) || phase.nome === movement?.title
    )) || savedPlan?.phases?.[0];
    const timings = timingOverride
      || (savedPhase ? {
        green: savedPhase.green,
        yellow: savedPhase.yellow ?? savedPlan.yellow,
        red: savedPhase.red,
      } : (intersection.id === selectedIntersection?.id ? (optimized || plan) : signal));
    const crossing = catalog.crossings.find((item) => item.signalId === signal?.id);
    const arms = (intersection.arms || []).map((arm) => {
      const flow = flowAt(catalog.roadById.get(arm.roadId) || { id: arm.roadId, base: arm.flow }, forecastMinute);
      return { bearing: arm.bearing, flow, capacity: arm.cap, vc: vcRatio(flow, arm.cap) };
    });
    return {
      id: intersection.id,
      signalId: signal?.id,
      name: signal?.name || intersection.name || 'Semaforo',
      lat: intersection.lat,
      lng: intersection.lng,
      green: Number(timings?.green || 30),
      yellow: Number(timings?.yellow || 3),
      red: Number(timings?.red || 30),
      maneuverId: movement?.id,
      maneuverTitle: movement?.title,
      timingSource: savedPhase ? 'latest-optimized-plan' : 'manual-or-catalog',
      crossingLength: crossing?.length,
      saturation: arms.reduce((max, arm) => Math.max(max, arm.vc), 0),
      arms,
    };
  }, [catalog, forecastMinute, optimized, optimizedPlans, plan, selectedIntersection]);

  const selectedCorridor = () => {
    const intersections = greenWaveSelection
      .map((id) => catalog.intersections.find((item) => item.id === id))
      .filter(Boolean);
    if (intersections.length >= 3) {
      const corridorBearing = bearingBetween(intersections[0], intersections[intersections.length - 1]);
      const offCorridor = intersections.slice(1, -1).some((item) => {
        const bearing = bearingBetween(intersections[0], item);
        const delta = Math.abs((((bearing - corridorBearing) + 540) % 360) - 180);
        return delta > 35;
      });
      if (offCorridor) {
        messageApi.warning('Seleziona semafori lungo lo stesso corridoio e nella stessa direzione.');
        return null;
      }
    }
    return intersections;
  };

  const openSyncfixConfig = () => {
    const intersections = selectedCorridor();
    if (!intersections || intersections.length < 2) return;
    const stops = intersections.map((intersection, index) => {
      const target = intersections[index + 1] || intersections[index - 1];
      const corridorBearing = target ? bearingBetween(intersection, target) : null;
      return buildCorridorStop(intersection, null, corridorBearing);
    });
    setSyncfixDraft({
      stops,
      distances: stops.slice(1).map((stop, index) => Math.max(1, Math.round(distanceMeters(stops[index], stop)))),
      speeds: stops.slice(1).map(() => progressionSpeed),
    });
  };

  const confirmSyncfixConfig = ({ distances, speeds, stops: configuredStops }) => {
    if (!syncfixDraft) return;
    const sourceStops = configuredStops?.length ? configuredStops : syncfixDraft.stops;
    const stops = sourceStops.map((stop, index) => ({
      ...stop,
      segmentDistance: index === 0 ? 0 : distances[index - 1],
      segmentSpeedKph: index === 0 ? speeds[0] : speeds[index - 1],
    }));
    setGreenWaveStops(stops);
    setGreenWaveSelection(stops.map((stop) => stop.id));
    setProgressionSpeed(speeds[0] || progressionSpeed);
    setGreenWaveMode(false);
    setSyncfixDraft(null);
  };

  const openSyncfixModule = (corridor, handoffOptions = {}) => {
    const selection = corridor || (selectedSignal ? [buildCorridorStop(selectedIntersection)] : null);
    if (!selection?.length) {
      messageApi.info('Seleziona un’intersezione semaforizzata per aprire SYNCFIX.');
      return;
    }
    // SYNCFIX expects the corridor ordered along the priority direction.
    const order = greenWave?.rows.map((row) => row.id) || [];
    const stops = order.length
      ? order.map((id) => selection.find((stop) => stop.id === id)).filter(Boolean)
      : selection;
    writeSemafixHandoff({
      ...handoffOptions,
      areaPolygon,
      measurements,
      openConfigModal: true,
      intersectionId: stops[0].id,
      signalId: stops[0].signalId,
      name: stops[0].name,
      lat: stops[0].lat,
      lng: stops[0].lng,
      green: stops[0].green,
      yellow: stops[0].yellow,
      red: stops[0].red,
      crossingLength: stops[0].crossingLength,
      speedKph: progressionSpeed,
      commonCycle: greenWave?.commonCycle,
      forecastMinute,
      corridor: stops.map((stop) => {
        const row = greenWave?.rows.find((item) => item.id === stop.id);
        return {
          intersectionId: stop.id,
          signalId: stop.signalId,
          name: stop.name,
          lat: stop.lat,
          lng: stop.lng,
          green: stop.green,
          yellow: stop.yellow,
          red: stop.red,
          cycle: stop.green + stop.yellow + stop.red,
          crossingLength: stop.crossingLength,
          saturation: stop.saturation,
          maneuverId: stop.maneuverId,
          maneuverTitle: stop.maneuverTitle,
          timingSource: stop.timingSource,
          offset: row?.offset ?? 0,
          distance: row?.distance ?? 0,
          travelTime: row?.travelTime ?? 0,
          flow: row?.flow ?? 0,
        };
      }),
    });
    sessionStorage.setItem('activateModule', 'tlgreenwave');
    navigate('/');
  };

  const openIntersectionEditor = (intersection) => {
    if (!intersection) return;
    const signal = catalog.signalByIntersection.get(intersection.id);
    const saved = optimizedPlans[intersection.id];
    const phase = saved?.phases?.[0];
    setEditorPlan(phase ? {
      green: phase.green,
      yellow: phase.yellow ?? saved.yellow,
      red: phase.red,
      cycle: saved.cycle,
    } : signal);
    setEditorIntersection(intersection);
    setSelectedIntersection(null);
    cancelGreenWave();
  };

  const canUseSyncfixModule = hasService('tl_green_wave');

  return (
    <div className={`semafix-page${editorIntersection ? ' semafix-page-split' : ''}${greenWave ? ' has-gw-card' : ''}`}>
      {contextHolder}
      <MapContainer
        center={CENTER}
        zoom={14}
        zoomControl={false}
        attributionControl={false}
        preferCanvas
        className={`semafix-map${measuring ? ' is-measuring' : ''}${addingTrafficLight ? ' is-adding-light' : ''}`}
      >
        <TraffixTileLayer theme={mapTheme} />
        <MapRefCapture mapRef={mapRef} onMapReady={setMainMap} />
        <FeatureGroup ref={areaGroupRef} />

        {flowsVisible && catalog.roads.map((road) => {
          const flow = flowAt(road, forecastMinute);
          const vc = vcRatio(flow, road.cap);
          // §2: a street picked from search or clicked turns purple, every other one grey.
          const isFocused = focusedRoadId === road.id;
          const muted = hasMapSelection && !isFocused;
          return (
            <Polyline
              key={road.id}
              positions={road.co}
              pathOptions={{
                color: selectionAwareColor(isFocused, hasMapSelection, vcColor(vc)),
                weight: muted ? 3 : roadWeight(road, vc) + (isFocused ? 3 : 0),
                opacity: hasMapSelection ? (isFocused ? 1 : 0.55) : 0.85,
              }}
              eventHandlers={{
                click: () => {
                  if (greenWaveMode || measuring) return;
                  setSelectedIntersection(null);
                  setSelectedCrossing(null);
                  setHighlightedRoadId(null);
                  setSelectedRoad(road);
                },
              }}
            >
              <Tooltip sticky>{road.n} · V/C {vc.toFixed(2)}</Tooltip>
            </Polyline>
          );
        })}

        {flowsVisible && focusedRoad && (
          <Polyline
            key={`focused-${focusedRoad.id}`}
            positions={focusedRoad.co}
            interactive={false}
            pathOptions={{
              color: MAP_SELECTED_COLOR,
              weight: roadWeight(focusedRoad, 1) + 3,
              opacity: 1,
            }}
          />
        )}

        {areaPolygon && visibleIntersections
          .filter((intersection) => nodeTypeFilters[intersection.geometryType || 'altro'])
          .map((intersection) => {
          const order = greenWaveSelection.indexOf(intersection.id);
          const isSignal = intersection.type === 'semaforizzata';
          return (
            <Marker
              key={intersection.id}
              position={[intersection.lat, intersection.lng]}
              icon={intersectionIcon(intersection, {
                selected: greenWaveMode ? order >= 0 : selectedIntersection?.id === intersection.id,
                dimmed: greenWaveMode && !isSignal,
                muted: !greenWaveMode && hasMapSelection,
                order: order >= 0 ? order + 1 : null,
                planStatus: optimizedPlans[intersection.id] ? 'optimized' : 'pending',
              })}
              zIndexOffset={isSignal ? 200 : 0}
              eventHandlers={{
                click: () => {
                  if (measuring) return;
                  if (addingTrafficLight) return;
                  if (greenWaveMode) toggleGreenWaveSelection(intersection);
                  else selectIntersection(intersection);
                },
              }}
            >
              <Tooltip direction="top" offset={[0, -6]}>
                {intersection.name || 'Intersezione'}
              </Tooltip>
            </Marker>
          );
        })}

        {catalog.crossings
          .filter((crossing) => visibleSignalIds.has(crossing.signalId))
          .map((crossing) => (
          <Marker
            key={crossing.id}
            position={[crossing.lat, crossing.lng]}
            icon={crossingIcon(crossing, {
              selected: selectedCrossing?.id === crossing.id,
              muted: !greenWaveMode && hasMapSelection,
            })}
            eventHandlers={{
              click: () => {
                if (greenWaveMode || measuring) return;
                setSelectedRoad(null);
                setHighlightedRoadId(null);
                setSelectedIntersection(null);
                setSelectedCrossing(crossing);
              },
            }}
          >
            <Tooltip direction="top" offset={[0, -6]}>{crossing.name} · rischio {crossing.risk}</Tooltip>
          </Marker>
        ))}

        <MeasurementLayer
          measurements={measurements}
          start={measureStart}
          cursor={measureCursor}
        />

        <div className="semafix-map-controls" ref={isolateFromMapClicks}>
          {!editorIntersection && (
            <div className="semafix-theme-toggle">
              <ThemeToggleButton theme={mapTheme} onChange={setMapTheme} />
            </div>
          )}
          <CurrentLocationButton />
          <ZoomButtons />
        </div>
      </MapContainer>

      {editorIntersection && (
        <IntersectionEditor
          intersection={editorIntersection}
          signal={catalog.signalByIntersection.get(editorIntersection.id)}
          signalPlan={editorPlan}
          measurements={measurements}
          measuring={measuring}
          crossings={catalog.crossings.filter((item) => item.signalId === catalog.signalByIntersection.get(editorIntersection.id)?.id)}
          onMeasuringChange={changeMeasuring}
          onMeasure={addMeasurement}
          onClose={() => {
            setEditorIntersection(null);
            setEditorPlan(null);
            setTimeout(() => {
              mapRef.current?.invalidateSize();
              mapRef.current?.flyTo([editorIntersection.lat, editorIntersection.lng], 16);
            }, 220);
          }}
        />
      )}

      {/* One column for every right-hand overlay: stacking them in flow
          instead of absolute offsets keeps them from ever overlapping. */}
      <div className="semafix-right-rail">
        <div className="semafix-top-controls">
          <AntTooltip title="Disegna area" placement="bottom">
            <button
              className={`sidebar-button ${drawing || areaPolygon ? 'active' : ''}`}
              onClick={areaPolygon ? clearArea : startAreaDraw}
              aria-label="Disegna area"
            >
              <TbPolygon />
            </button>
          </AntTooltip>
          <AntTooltip title="Cerca strada o semaforo" placement="bottom">
            <button
              className={`sidebar-button ${searchOpen ? 'active' : ''}`}
              onClick={() => setSearchOpen((current) => !current)}
              aria-label="Cerca"
              aria-pressed={searchOpen}
            >
              <SearchOutlined />
            </button>
          </AntTooltip>
          {areaPolygon && (
            <AntTooltip title="Misura distanza" placement="bottom">
              <button
                className={`sidebar-button ${measuring ? 'active' : ''}`}
                onClick={() => changeMeasuring(!measuring)}
                aria-label="Misura distanza"
                aria-pressed={measuring}
              >
                <TbRulerMeasure />
              </button>
            </AntTooltip>
          )}
          <AntTooltip title={areaPolygon ? 'Aggiungi semaforo' : 'Disegna prima un’area'} placement="bottom">
            <button
              className={`sidebar-button ${addingTrafficLight ? 'active' : ''}`}
              disabled={!areaPolygon}
              onClick={() => {
                setMeasuring(false);
                setAddingTrafficLight((current) => !current);
              }}
              aria-label="Aggiungi semaforo"
              aria-pressed={addingTrafficLight}
            >
              <img src={TlGreenWaveIcon} alt="" />
            </button>
          </AntTooltip>
          <AntTooltip title="SYNCFIX · onda verde" placement="bottom">
            <button
              className={`sidebar-button ${greenWaveMode ? 'active' : ''}`}
              onClick={() => activateGreenWave()}
              aria-label="Apri SYNCFIX"
              aria-pressed={greenWaveMode}
            >
              <img src={TlGreenWaveIcon} alt="" />
            </button>
          </AntTooltip>
          <AntTooltip title="Adatta alla rete" placement="bottom">
            <button className="sidebar-button" onClick={fitNetwork} aria-label="Adatta alla rete">
              <MdCenterFocusStrong />
            </button>
          </AntTooltip>
          <AntTooltip title="Reset" placement="bottom">
            <button className="sidebar-button" onClick={reset} aria-label="Reset">
              <MdRefresh />
            </button>
          </AntTooltip>
        </div>

        {/* Semafix v2 §15: the network-wide time/scenario slider is removed. The
            map shows current conditions (V/C); forecast is available per link in
            the segment panel chart. `forecastMinute` stays at LIVE (0). */}

        {searchOpen && (
        <SemafixFilterBar
          roads={catalog.roads}
          intersections={catalog.intersections.filter((item) => catalog.signalByIntersection.has(item.id))}
          onLocationSelect={({ coordinates }) => {
            if (coordinates) mapRef.current?.flyTo(coordinates, 16);
          }}
          onRoadSelect={(road) => {
            if (!road) { setHighlightedRoadId(null); return; }
            setHighlightedRoadId(road.id);
            setSelectedRoad(road);
            setSelectedIntersection(null);
            setSelectedCrossing(null);
            const bounds = L.latLngBounds(road.co);
            if (bounds.isValid()) mapRef.current?.fitBounds(bounds, { padding: [80, 80] });
          }}
          onIntersectionSelect={(intersection) => {
            selectIntersection(intersection);
            mapRef.current?.flyTo([intersection.lat, intersection.lng], 18);
          }}
        />
        )}

        {(measuring || measurements.length > 0) && (
          <div className="semafix-measure-card">
            {measuring && (
              <p className="semafix-measure-hint">
                {measureStart
                  ? 'Clicca il secondo punto su questa mappa.'
                  : 'Clicca il punto iniziale e poi il punto finale sulla mappa.'}
              </p>
            )}
            <MeasurementList
              measurements={measurements}
              onChange={updateMeasurement}
              onAdd={addManualMeasurement}
              onRemove={removeMeasurement}
              onUndo={undoMeasurement}
              onClear={clearMeasurements}
              onFocus={(measurement) => mapRef.current?.fitBounds(
                L.latLngBounds([measurement.from, measurement.to]),
                { padding: [80, 80] },
              )}
            />
          </div>
        )}
      </div>

      {greenWaveMode ? (
        <SyncfixModeBar
          count={greenWaveSelection.length}
          onCalculate={openSyncfixConfig}
          onCancel={cancelGreenWave}
        />
      ) : (drawing || areaPolygon) && (
        <div className="semafix-banner">
          {addingTrafficLight
            ? 'Clicca nell’area per aggiungere il semaforo mancante'
            : drawing
            ? 'Disegna un’area sulla mappa per estrarre le intersezioni'
            : `${visibleIntersections.length} intersezioni semaforizzate individuate nell’area`}
        </div>
      )}

      <SemafixFlowLegend flowsVisible={flowsVisible} onToggleFlows={setFlowsVisible} />
      <SemafixLegend
        nodeTypeFilters={nodeTypeFilters}
        onToggleNodeType={toggleNodeType}
        nodeTypeCounts={nodeTypeCounts}
      />

      <SyncfixConfigModal
        open={Boolean(syncfixDraft)}
        stops={syncfixDraft?.stops || []}
        distances={syncfixDraft?.distances || []}
        speeds={syncfixDraft?.speeds || []}
        optimizedIds={optimizedPlans}
        onOptimize={optimizeCorridorStop}
        onCancel={() => setSyncfixDraft(null)}
        onConfirm={confirmSyncfixConfig}
      />

      <GreenWaveCard
        wave={greenWave}
        onSpeedChange={(value) => {
          setProgressionSpeed(value);
          setGreenWaveStops((current) => (
            current ? current.map((stop) => ({ ...stop, segmentSpeedKph: null })) : current
          ));
        }}
        canOpenSyncfixModule={canUseSyncfixModule}
        onOpenSyncfixModule={() => openSyncfixModule(greenWaveStops)}
        onOptimizeMissing={() => {
          const missing = greenWave?.rows.find((row) => row.timingSource !== 'latest-optimized-plan');
          openIntersectionEditor(catalog.intersections.find((item) => item.id === missing?.id));
        }}
        onClose={cancelGreenWave}
      />

      <SegmentPanel road={selectedRoad} flow={roadFlow} vc={roadVc} onClose={() => { setSelectedRoad(null); setHighlightedRoadId(null); }} />
      <IntersectionPanel
        intersection={selectedIntersection}
        signal={selectedSignal}
        arms={armMetrics}
        plan={plan}
        optimized={optimized}
        crossings={signalCrossings}
        maneuvers={selectedIntersection?.maneuversPreview || []}
        onOpenEditor={() => openIntersectionEditor(selectedIntersection)}
        onClose={() => setSelectedIntersection(null)}
      />
      <PedestrianPanel crossing={selectedCrossing} onClose={() => setSelectedCrossing(null)} />

      <ModuleSlider
        showNavbarControls={false} setShowNavbarControls={() => { }}
        showSimfixAreas={false} setShowSimfixAreas={() => { }}
        showPredictMode={false} setShowPredictMode={() => { }}
        showTlGreenWave={false} setShowTlGreenWave={() => { }}
        showAssignx={false} setShowAssignx={() => { }}
      />

      {loading && (
        <div className="semafix-loading">
          <div className="semafix-loading-card">
            <Spin size="large" />
            <p>Caricamento rete SEMAFIX…</p>
          </div>
        </div>
      )}
    </div>
  );
}
